import React, { useEffect, useMemo, useRef } from 'react';
import Dropdown from '../ui/Dropdown';
import { TOOLBAR_DISCLOSURE_TRIGGER } from '../ui/Toolbar';
import { MENU_ITEM_NEUTRAL, MENU_SECTION_HEADING_TEXT } from '../OrderActionsMenu';
import useSharePdf from '../../hooks/useSharePdf';

/**
 * Manage Budgets' ONE `Actions ▾` — owner ruling 2026-10-05 (*"fab lets build
 * the preview"*), replacing BOTH the Cost Codes section-header menu (d32adbc9)
 * and the band's separate `Download / Print` button. It sits at the RIGHT END
 * OF THE TABS ROW, the house placement (`components/ui/CLAUDE.md`: *"put the
 * action menu on the same tab row but keep it on the right"*).
 *
 * Its rules (groups, `All`, `printOnly`, resolves nothing) → `components/budget/CLAUDE.md` (Manage Budgets), moved there 2026-10-28.
 */

/** Every tab ticked? (`All` is this, derived.) */
export function allTicked(ticks, tabIds) {
  return tabIds.length > 0 && tabIds.every((id) => !!ticks[id]);
}

/** `All` clicked: if everything is ticked, untick everything; else tick everything. */
export function toggleAllTicks(ticks, tabIds) {
  const next = !allTicked(ticks, tabIds);
  const out = {};
  for (const id of tabIds) out[id] = next;
  return out;
}

/** One tab clicked. */
export function toggleTick(ticks, id) {
  return { ...ticks, [id]: !ticks[id] };
}

/** The ticked tab ids, in TAB order (the PDF's section order). */
export function tickedKeys(ticks, tabIds) {
  return tabIds.filter((id) => !!ticks[id]);
}

/** Seed: only the current tab ticked. */
export function seedTicks(tabIds, currentTabId) {
  const out = {};
  for (const id of tabIds) out[id] = id === currentTabId;
  return out;
}

const CHECK_ROW =
  'w-full flex items-center gap-2 text-left px-3 py-1.5 min-h-[44px] md:min-h-0 rounded-md text-sm ' +
  'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors';

function TickBox({ checked }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-flex items-center justify-center h-4 w-4 shrink-0 rounded border ${
        checked
          ? 'bg-blue-600 border-blue-600 text-white dark:bg-blue-500 dark:border-blue-500'
          : 'bg-white border-gray-400 dark:bg-gray-800 dark:border-gray-500'
      }`}
    >
      {checked && (
        <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={2.5}>
          <path d="M3.5 8.5l3 3 6-7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  );
}

export const NOTHING_TICKED_REASON = 'Tick at least one section to print or download';

export default function BudgetManageActionsMenu({
  tabs,
  currentTabId,
  currentTabLabel,
  currentActions = null,
  /* Table utilities drawn LAST, after the print group — `Fit columns to
     screen`, matching `hires/HireListActionsMenu.js`'s order (owner, 2026-10-05:
     "match the menu wording"). Same `({ close, itemClassName })` contract. */
  trailingActions = null,
  printOnly = false,
  buildPdf,
  filename,
  title,
  testIdPrefix = 'budget-actions',
}) {
  const tabIds = useMemo(() => tabs.map((t) => t.id), [tabs]);
  const extras = useMemo(
    () => tabs.map((t) => ({
      key: t.id,
      label: t.label,
      defaultActive: t.id === currentTabId,
      filenameSuffix: t.filenameSuffix || '',
    })),
    // The seed follows the current tab through the effect below, not through
    // the extras list — re-seeding on a key-list change only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tabs]
  );

  // The panel's close(), captured per render so a COMPLETED download/print
  // dismisses it (useSharePdf's onActionComplete) and a failed print does not.
  const closeRef = useRef(null);
  const {
    activeExtras, setActiveExtras, nothingActive, pendingAction,
    handleDownload, handlePrint, resetCache,
  } = useSharePdf({
    extras,
    filename,
    title,
    orientation: 'landscape',
    renderPdfMakeDoc: ({ activeExtras: state }) => buildPdf(tickedKeys(state, tabIds)),
    onActionComplete: () => { if (closeRef.current) closeRef.current(); },
  });

  useEffect(() => {
    setActiveExtras(seedTicks(tabIds, currentTabId));
    resetCache();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTabId, tabIds.join('|')]);

  const all = allTicked(activeExtras, tabIds);
  const tid = (s) => `${testIdPrefix}-${s}`;

  return (
    <Dropdown
      align="right"
      haspopup="menu"
      panelClassName="w-72 p-1"
      triggerClassName={TOOLBAR_DISCLOSURE_TRIGGER}
      title="Budget actions — this tab's table, and print or download"
      ariaLabel="Budget actions"
      triggerTestId={tid('menu')}
      label="Actions"
    >
      {({ close }) => { closeRef.current = close; return (
        <div role="menu" aria-label="Budget actions">
          {!printOnly && currentActions && (
            <div role="group" aria-labelledby={tid('tab-heading')} data-testid={tid('tab-group')}>
              <div id={tid('tab-heading')} className={`px-3 pt-1.5 pb-1 ${MENU_SECTION_HEADING_TEXT}`}>
                {currentTabLabel}
              </div>
              {currentActions({ close, itemClassName: MENU_ITEM_NEUTRAL })}
              <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
            </div>
          )}
          <div role="group" aria-labelledby={tid('print-heading')} data-testid={tid('print-group')}>
            <div id={tid('print-heading')} className={`px-3 pt-1.5 pb-1 ${MENU_SECTION_HEADING_TEXT}`}>
              Print / Download
            </div>
            <button
              type="button"
              role="menuitemcheckbox"
              aria-checked={all}
              onClick={(e) => { e.stopPropagation(); setActiveExtras((p) => toggleAllTicks(p, tabIds)); }}
              className={`${CHECK_ROW} font-medium`}
              data-testid={tid('tick-all')}
            >
              <TickBox checked={all} />
              All
            </button>
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                role="menuitemcheckbox"
                aria-checked={!!activeExtras[t.id]}
                onClick={(e) => { e.stopPropagation(); setActiveExtras((p) => toggleTick(p, t.id)); }}
                className={`${CHECK_ROW} pl-6`}
                data-testid={tid(`tick-${t.id}`)}
              >
                <TickBox checked={!!activeExtras[t.id]} />
                {t.label}
              </button>
            ))}
            <div className="flex gap-2 px-2 pt-2 pb-1.5">
              <button
                type="button"
                role="menuitem"
                onClick={(e) => { e.stopPropagation(); handleDownload(); }}
                disabled={nothingActive || !!pendingAction}
                title={nothingActive ? NOTHING_TICKED_REASON : 'Download the ticked sections as one landscape PDF'}
                className={`${MENU_ITEM_NEUTRAL} flex-1 justify-center border border-gray-300 dark:border-gray-600`}
                data-testid={tid('download-pdf')}
              >
                {pendingAction === 'download' ? 'Generating…' : 'Download PDF'}
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={(e) => { e.stopPropagation(); handlePrint(); }}
                disabled={nothingActive || !!pendingAction}
                title={nothingActive ? NOTHING_TICKED_REASON : 'Print the ticked sections as one landscape PDF'}
                className={`${MENU_ITEM_NEUTRAL} flex-1 justify-center border border-gray-300 dark:border-gray-600`}
                data-testid={tid('print')}
              >
                {pendingAction === 'print' ? 'Generating…' : 'Print all'}
              </button>
            </div>
            {nothingActive && (
              <p className="px-3 pb-1.5 text-xs text-gray-500 dark:text-gray-400" data-testid={tid('nothing-ticked')}>
                {NOTHING_TICKED_REASON}.
              </p>
            )}
          </div>
          {!printOnly && trailingActions && (
            <div data-testid={tid('trailing-group')}>
              <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
              {trailingActions({ close, itemClassName: MENU_ITEM_NEUTRAL })}
            </div>
          )}
        </div>
      ); }}
    </Dropdown>
  );
}
